import { UploadedFileMeta, uploadMessageAttachment } from './uploads';
import { fetchWithAuth } from '../lib/fetchWithAuth';

const API_BASE_URL = import.meta.env.VITE_API_URL?.replace(/\/$/, '');

export type ConversationSummary = {
  id: string;
  candidateId: string;
  candidateName: string;
  candidateTitle: string;
  jobTitle: string;
  lastMessage: string;
  lastMessageTime: string;
  unread: number;
};

export type ConversationMessage = {
  id: string;
  conversationId: string;
  sender: 'candidate' | 'employer';
  body: string;
  createdAt: string;
  attachments?: UploadedFileMeta[];
};

export async function fetchConversations(): Promise<ConversationSummary[]> {
  if (!API_BASE_URL) {
    throw new Error('VITE_API_URL is required to load conversations.');
  }
  const response = await fetchWithAuth(`${API_BASE_URL}/v1/conversations`);
  if (!response.ok) {
    const errorText = await response.text().catch(() => 'request failed');
    throw new Error(`Conversations fetch failed (${response.status}): ${errorText}`);
  }
  const payload = await response.json();
  return (payload?.conversations as ConversationSummary[]) ?? [];
}

export async function fetchConversationMessages(conversationId: string): Promise<ConversationMessage[]> {
  if (!API_BASE_URL) {
    throw new Error('VITE_API_URL is required to load conversation messages.');
  }
  const response = await fetchWithAuth(`${API_BASE_URL}/v1/conversations/${conversationId}/messages`);
  if (!response.ok) {
    const errorText = await response.text().catch(() => 'request failed');
    throw new Error(`Messages fetch failed (${response.status}): ${errorText}`);
  }
  const payload = await response.json();
  return (payload?.messages as ConversationMessage[]) ?? [];
}

export async function sendConversationMessage(
  conversationId: string,
  body: string,
  attachments?: UploadedFileMeta[],
): Promise<ConversationMessage> {
  if (!API_BASE_URL) {
    throw new Error('VITE_API_URL is required to send conversation messages.');
  }

  const response = await fetchWithAuth(`${API_BASE_URL}/v1/conversations/${conversationId}/messages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ body, attachments }),
  });
  if (!response.ok) {
    const errorText = await response.text().catch(() => 'request failed');
    throw new Error(`Message send failed (${response.status}): ${errorText}`);
  }
  return (await response.json()) as ConversationMessage;
}

export function subscribeToConversationEvents(
  conversationId: string,
  onEvent: (data: { type: string; [key: string]: unknown }) => void,
) {
  if (!API_BASE_URL || typeof window === 'undefined') {
    console.warn('[conversations] missing API base for event subscription');
    return () => {};
  }
  const eventSource = new EventSource(`${API_BASE_URL}/v1/conversations/${conversationId}/events`);
  eventSource.onmessage = (event) => {
    onEvent(JSON.parse(event.data));
  };
  eventSource.onerror = () => {
    eventSource.close();
  };
  return () => eventSource.close();
}

export async function uploadConversationAttachment(file: File) {
  return uploadMessageAttachment(file);
}
